import { Breadcrumb, PageHeaderLayout, RouteTabWithContent, TabContent } from "@homebound/beam";
import { useMemo } from "react";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { EmptyState } from "src/routes/components/EmptyState";
import { EmptyPlanAndOptionImage } from "src/routes/developments/plan-and-options/components/EmptyStateSvgs";
import { createHierarchyBreadcrumbs } from "src/routes/projects/ProjectHeader";
import { ProjectParams } from "src/routes/routesDef";
import {
  createProjectCostAnalysisComparisonUrl,
  createProjectCostAnalysisOptionsUrl,
  createProjectCostAnalysisUrl,
  createProjectUrl,
} from "src/RouteUrls";
import {
  projectCostAnalysisComparisonRoute,
  projectCostAnalysisOptionsRoute,
  projectCostAnalysisRoute,
} from "src/routing/domains/projectsPaths";
import { queryResult } from "src/utils";
import { AllConfigurationsTab } from "./AllConfigurationsTab";
import { CostAnalysisPageQuery, useCostAnalysisPageQuery } from "./CostAnalysisPage.generated";

export function CostAnalysisPage() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useCostAnalysisPageQuery({ variables: { projectId } });
  return queryResult(query, {
    data: ({ project }) => <CostAnalysisPageView projectId={projectId} project={project} />,
  });
}

type CostAnalysisPageViewProps = { projectId: string; project: CostAnalysisPageQuery["project"] };

function CostAnalysisPageView({ projectId, project }: CostAnalysisPageViewProps) {
  // Only communities have product offerings, so any other project type gets an empty table
  const productOfferings = useMemo(
    () =>
      (project.__typename === "Community" ? project.productOfferings : []).filter(
        (po) => po.effectiveStatus.code === ReadyPlanStatus.Active,
      ),
    [project],
  );
  const tabs: RouteTabWithContent[] = useMemo(
    () => [
      {
        name: "All Configurations",
        href: createProjectCostAnalysisUrl(projectId),
        path: projectCostAnalysisRoute.path,
        render: () => <AllConfigurationsTab productOfferings={productOfferings} />,
      },
      {
        name: "Configuration Comparison",
        href: createProjectCostAnalysisComparisonUrl(projectId),
        path: projectCostAnalysisComparisonRoute.path,
        render: () => <EmptyState message="Coming soon" svg={<EmptyPlanAndOptionImage />} />,
      },
      {
        name: "Options",
        href: createProjectCostAnalysisOptionsUrl(projectId),
        path: projectCostAnalysisOptionsRoute.path,
        render: () => <EmptyState message="Coming soon" svg={<EmptyPlanAndOptionImage />} />,
      },
    ],
    [productOfferings, projectId],
  );
  const breadcrumbs: Breadcrumb[] = [
    ...createHierarchyBreadcrumbs(project, createProjectUrl),
    { label: "Cost Analysis", href: createProjectCostAnalysisUrl(projectId) },
  ];
  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Cost Analysis",
        documentTitleSuffix: project.name,
        breadcrumbs: { breadcrumbs },
        tabs: { tabs },
      }}
    >
      <TabContent tabs={tabs} />
    </PageHeaderLayout>
  );
}
